import type { SetupIntent } from '@stripe/stripe-js';

import type { BillingAccount, BillingInfoRequest, ElementsCredentials, StripeCustomer } from '@ctct/domain-layer';
import { BillingStrategy } from '@ctct/domain-layer';
import type { Payment, StripeElementsConfig, StripePayment } from '@ctct/infrastructure-layer';
import { stripeApi } from '@ctct/infrastructure-layer';
import { observabilityAppService } from '@ctct/myaccount-utils-static';
import { PaymentMethodStripeEditState, type PaymentMethodsPageStore } from '@ctct/state-client-layer';
import type { UpdateSetupIntentParamsMutation } from '@ctct/state-server-layer';

import type { PaypalService } from './PaypalService';
import { paymentMethodsEventBus } from '../PaymentMethodsEventBus';
import type { StripeBillingInfo } from '../types';

type UpdateBillingPaymentInfo = NonNullable<BillingInfoRequest['payment_info']>;

type StripeLocaleConfig = {
  countryCode: string | null;
  currencyCode: string | null;
};

// we keep track of the current stripe locale config state to avoid sendting multiple mutations with the same params
let currentStripeLocaleConfigState: StripeLocaleConfig = {
  countryCode: null,
  currencyCode: null,
};

export class StripeService {
  constructor(
    private pageStore: PaymentMethodsPageStore,
    private elementsCredentials: ElementsCredentials,
    private paypal: PaypalService,
    private stripeCustomer: StripeCustomer | null,
    private billingAccount: BillingAccount,
    private billingStrategy: BillingStrategy,
    private updateSetupIntentParamsMutation: UpdateSetupIntentParamsMutation,
    stripeLocaleCountryCode: string | null,
    private isImpersonated = false,
    private refetchRating?: () => Promise<unknown>
  ) {
    if (!currentStripeLocaleConfigState.countryCode && stripeLocaleCountryCode) {
      currentStripeLocaleConfigState = {
        countryCode: stripeLocaleCountryCode,
        currencyCode: this.billingAccount.getCurrency() ?? null,
      };
    }

    this.updateStripeLocaleConfig({
      countryCode: currentStripeLocaleConfigState.countryCode,
      currencyCode: this.billingAccount.getCurrency() ?? null,
    });
  }

  public api() {
    return {
      getPayloadForCheckoutApi: (args: StripeBillingInfo) => this.getPayloadForCheckoutApi(args),
      getPayloadForUpdateBillingInfoApi: (args: StripeBillingInfo) => this.getPayloadForUpdateBillingInfoApi(args),

      /** @see https://github.roving.com/ES/myact-react-static/blob/93642f4f22c0b5b6aa518fa7b247025adaf54981/libs/application-layer/src/js/managers/PaymentMethodsManager/services/InHouseApiPayloadService.ts#L96-L97 */
      getPayloadForOverduePayment: async (args: StripeBillingInfo) => {
        const { payPal, ...rest } = await this.getPayloadForCheckoutApi(args);

        return {
          ...rest,
          paypal: payPal,
        };
      },
      setStripeLocaleConfig: (stripeLocaleConfig: Partial<StripeLocaleConfig>) =>
        this.updateStripeLocaleConfig(stripeLocaleConfig),
      handleExternalPaymentSubmission: () => this.handleExternalPaymentSubmission(),
      updateBillingCountry: (countryCode: string) => this.updateStripeLocaleConfig({ countryCode }),
      setStripeEditStateToEditable: () => this.onShowStripeEditElement(),
      handleStripePaymentFailure: () => this.handleStripePaymentFailure(),
    };
  }

  public data() {
    return {
      getPaymentMethods: () => this.getPaymentMethods(),
      getPaymentMethodOrder: () => this.getPaymentMethodOrder(),
      getIsUpdatingStripeLocaleConfig: (paymentMethodsUpdatingStripeLocaleConfig: boolean) =>
        this.isUpdatingStripeLocaleConfig(paymentMethodsUpdatingStripeLocaleConfig),
    };
  }

  public handlers() {
    return {
      onEditExistingPaymentMethod: () => this.onShowStripeEditElement(),
      onCancelEditExistingPaymentMethod: () => this.onShowSavedPaymentMethod(),

      /**
       * Handle when the Stripe Payment Element is mounted.
       *
       * This happens on initial page load for trialers, or when a customer clicks the "Edit" button.
       */
      onMountStripeElementDiv: () => this.mountStripeElement(),
    };
  }

  private getPaymentMethods(): StripeElementsConfig['customPaymentMethods'] {
    const { paypal } = stripeApi.getPaymentMethodIds();

    return [
      {
        id: paypal,
        options: {
          type: 'static',
        },
      },
    ];
  }

  private getPaymentMethodOrder(): string[] {
    const { card, usBankAccount, paypal } = stripeApi.getPaymentMethodIds();

    return [card, usBankAccount, paypal];
  }

  private async getPayloadForCheckoutApi({
    postalCode,
    countryCode,
    fullName,
    email,
    phone,
  }: StripeBillingInfo): Promise<Payment> {
    const { paymentMethodStripeState } = this.pageStore.getState();

    if (this.getStripePaymentMethodEditState() === PaymentMethodStripeEditState.SavedPaymentMethod) {
      const token = this.getPresavedPaymentToken();

      return {
        paymentType: {
          name: 'STRIPE',
          displayString: 'Stripe',
        },
        stripe: { token },
        creditCard: null,
        check: null,
        echeck: null,
        payPal: null,
        amazonPay: null,
        origin: null,
        commitDirective: null,
      };
    }

    if (paymentMethodStripeState.type === 'confirmed' && paymentMethodStripeState.paymentMethodId) {
      const { paymentMethodId: token } = paymentMethodStripeState;

      return {
        paymentType: {
          name: 'STRIPE',
          displayString: 'Stripe',
        },
        stripe: { token },
        creditCard: null,
        check: null,
        echeck: null,
        payPal: null,
        amazonPay: null,
        origin: null,
        commitDirective: null,
      };
    }

    const paypalAgreementId = this.paypal.api().getPaypalAgreementId();

    if (paypalAgreementId) {
      return {
        paymentType: { name: 'PAYPAL', displayString: 'Paypal' },
        stripe: null,
        creditCard: null,
        check: null,
        echeck: null,
        payPal: { paypalAgreementId },
        amazonPay: null,
        origin: null,
        commitDirective: null,
      };
    }

    const stripePaymentDetails = await this.getStripePaymentDetails({
      postalCode,
      countryCode,
      fullName,
      email,
      phone,
    });

    if (!stripePaymentDetails) {
      observabilityAppService.notify('#stripe: could not load payment details from Stripe Element');
      throw new Error('No payment method found');
    }

    return {
      paymentType: {
        name: 'STRIPE',
        displayString: 'Stripe',
      },
      stripe: stripePaymentDetails,
      creditCard: null,
      check: null,
      echeck: null,
      payPal: null,
      amazonPay: null,
      origin: null,
      commitDirective: null,
    };
  }

  /**
   * Builds the `payment_info` portion of the "update billing info" request for Stripe Payments customers.
   *
   * Confirms the Stripe setup (or reads the PayPal agreement) via the same logic used at checkout, then maps the
   * result to the billing-info request shape.
   */
  private async getPayloadForUpdateBillingInfoApi(args: StripeBillingInfo): Promise<UpdateBillingPaymentInfo> {
    const payment = await this.getPayloadForCheckoutApi(args);

    const base = {
      payment_type: null,
      credit_card_info: null,
      echeck_info: null,
      check: null,
      paypal: null,
      amazonpay: null,
      stripe: null,
    };

    if (payment.stripe) {
      return { ...base, payment_type: 'stripe', stripe: { token: payment.stripe.token } };
    }

    const paypalAgreementId = this.paypal.api().getPaypalAgreementId();

    if (payment.payPal && paypalAgreementId) {
      return { ...base, payment_type: 'paypal', paypal: { agreement_id: paypalAgreementId } };
    }

    observabilityAppService.notify('#stripe: unexpected payment payload when updating billing info');
    throw new Error('No payment method found');
  }

  private async getStripePaymentDetails({
    postalCode,
    countryCode,
    fullName,
    email,
    phone,
  }: StripeBillingInfo): Promise<StripePayment | null> {
    if (this.getStripePaymentMethodEditState() === PaymentMethodStripeEditState.SavedPaymentMethod) {
      const token = this.getPresavedPaymentToken();

      return { token };
    }

    const stripeElementFacade = await stripeApi.getStripeElementsFacade();

    const { error, paymentMethodId } = await stripeElementFacade.submit({
      returnUrl: stripeApi.getReturnUrl(),
      postalCode,
      countryCode,
      fullName,
      email,
      phone,
    });

    if (error) {
      throw new Error(error.message);
    }

    if (!paymentMethodId) {
      throw new Error('Could not create payment method');
    }

    return { token: paymentMethodId };
  }

  public async onMount() {
    // Check for PayPal token in URL early to prevent flashing credit card component
    const hasPaypalToken = this.paypal.api().hasPaypalTokenInUrl();

    if (hasPaypalToken) {
      this.onShowNewPaymentMethodNonEditable();
    }

    const wasSuccessfullyInitialized = await this.paypal.api().initPaypalTokenFromUrl();

    if (!wasSuccessfullyInitialized) {
      this.onShowStripeEditElement();
    }

    const stripeParams = stripeApi.readPostRedirectUrlParams();

    if (stripeParams) {
      const setupIntent = await stripeApi.retrieveSetupIntent(stripeParams.setupIntentClientSecret);

      if (setupIntent) {
        await this.handleSetupIntentStatus(setupIntent);

        return;
      }
    }

    if (this.paypal.getClientSidePaypalAgreementId() && wasSuccessfullyInitialized) {
      // Keep non-editable state (already set if hasPaypalToken was true)
      this.onShowNewPaymentMethodNonEditable();

      return;
    }

    if (this.stripeCustomer?.getPaymentMethod()) {
      this.onShowSavedPaymentMethod();

      return;
    }

    // Only show editable form if we haven't already set a different state
    if (!hasPaypalToken) {
      this.onShowStripeEditElement();
    }
  }

  private async mountStripeElement() {
    const stripeElementFacade = await stripeApi.getStripeElementsFacade();

    await stripeElementFacade.createAndMountPaymentElement(
      {
        clientSecret: this.elementsCredentials.clientSecret,
        customerSessionClientSecret: this.elementsCredentials.customerSessionClientSecret,
        customPaymentMethods: this.getPaymentMethods(),
        appearance: stripeApi.getDefaultStripeAppearance(),
      },
      {
        paymentMethodOrder: this.getPaymentMethodOrder(),
        layout: 'tabs',
        terms: {
          card: 'never',
          usBankAccount: 'never',
        },
        wallets: { applePay: 'auto', link: this.isImpersonated ? 'never' : 'auto' },
        fields: {
          billingDetails: {
            name: 'never',
            email: 'never',
            phone: 'never',
            address: {
              country: 'never',
              postalCode: 'never',
            },
          },
        },
      }
    );

    stripeElementFacade.addChangeListener((event) => {
      paymentMethodsEventBus.emit(
        'stripePaymentMethodChanged',
        { paymentMethod: event.paymentMethodType },
        { source: 'user' }
      );
    });
  }

  private async handleSetupIntentStatus(setupIntent: SetupIntent): Promise<void> {
    // @see https://docs.stripe.com/payments/paymentintents/lifecycle#intent-statuses

    switch (setupIntent.status) {
      case 'succeeded':
      case 'processing': {
        this.pageStore.setState({
          paymentMethodStripeState: {
            type: 'confirmed',
            paymentMethodId: setupIntent.payment_method as string | null,
          },
        });

        paymentMethodsEventBus.emit('stripePostRedirectSuccess', { source: 'onMount' });

        return;
      }

      case 'requires_payment_method':
        // Indicates a failed payment attempt (e.g. declined card).
        paymentMethodsEventBus.emit(
          'stripePostRedirectFailure',
          {
            message: 'We are sorry, there was an error processing your payment. Please try again.',
          },
          { source: 'onMount' }
        );
        break;

      case 'requires_confirmation':
        // We should never get here, since payment information is submitted at the same time that the payment is
        // confirmed.
        paymentMethodsEventBus.emit(
          'stripePostRedirectFailure',
          {
            message: 'Payment information is being processed; please try again.',
          },
          { source: 'onMount' }
        );
        break;

      case 'canceled':
        paymentMethodsEventBus.emit(
          'stripePostRedirectFailure',
          {
            message: 'Payment canceled; please try again.',
          },
          { source: 'onMount' }
        );
        break;

      case 'requires_action': {
        await stripeApi.handleNextAction(setupIntent);

        break;
      }

      default:
        break;
    }
  }

  /**
   * This method gets called when the user clicks the "Pay Now" button, but also when the browser returns
   * to the page after a redirect (e.g. after the user has logged into PayPal and accepted the terms, or if Stripe
   * needs to redirect the user to a 3DS page).
   *
   * In the latter case, we need to make sure that we don't ever call `this.getStripeSelectedPaymentMethod`
   * because we don't mount a Stripe Elements instance in this case, and it would throw an error.
   *
   * @returns `true` if checkout can proceed; `false` if we are interrupting the flow (e.g. to redirect the user to PayPal for completion)
   */
  private async handleExternalPaymentSubmission() {
    if (this.paypal.api().getPaypalAgreementId()) {
      // we have just returned from PayPal redirect
      return true;
    }

    if (this.pageStore.getState().paymentMethodStripeState.type === 'confirmed') {
      // we have just returned from Stripe redirect
      return true;
    }

    const { paypal } = stripeApi.getPaymentMethodIds();

    // Get state and customer session info upfront for validation logic below
    const state = this.pageStore.getState().paymentMethodStripeEditState;
    const hasCustomerSession =
      this.elementsCredentials.customerSessionClientSecret &&
      this.elementsCredentials.customerSessionClientSecret.length > 0;

    // Report unexpected state combinations to Airbrake before attempting to get payment method
    // This catches edge cases where we have a customer session but are in the wrong state
    if (hasCustomerSession && state !== PaymentMethodStripeEditState.Editable) {
      observabilityAppService.notify('#stripe: unexpected payment method state with customer session', {
        state: String(state),
      });
    }

    const paymentMethodId = await this.getStripeSelectedPaymentMethod();

    // When using customer sessions with saved payment methods, paymentMethodId might be null
    // even though a payment method is available. In this case, we should still proceed - the
    // payment method will be determined when submit() → confirmSetup() is called later in the
    // checkout flow.
    if (!paymentMethodId) {
      // Only allow null payment method if we're in Editable state AND have a customer session
      if (state === PaymentMethodStripeEditState.Editable && hasCustomerSession) {
        return true;
      }

      // No payment method and no customer session - user needs to enter payment info
      throw new Error('No payment method selected');
    }

    if (paymentMethodId === paypal) {
      this.paypal.api().continueTransactionWithPaypal();

      return false;
    }

    return true;
  }

  /**
   * Gets the currently selected payment method, without submitting the element.
   *
   * This is necessary for PayPal, because if you try to `submit` while PayPal is selected, Stripe will throw
   * an error.
   */
  private async getStripeSelectedPaymentMethod() {
    const state = this.pageStore.getState().paymentMethodStripeEditState;

    switch (state) {
      case PaymentMethodStripeEditState.Editable: {
        const stripeElementFacade = await stripeApi.getStripeElementsFacade();

        return stripeElementFacade.getSelectedPaymentMethod();
      }
      case PaymentMethodStripeEditState.SavedPaymentMethod:
        return this.getPresavedPaymentToken();

      case PaymentMethodStripeEditState.NewPaymentMethodNonEditable: {
        if (this.paypal.api().getPaypalAgreementId()) {
          return stripeApi.getPaymentMethodIds().paypal;
        }

        observabilityAppService.notify('#stripe: unexpected payment state post submission');
        throw new Error('Cannot process payment method');
      }
      default: {
        observabilityAppService.notify('#stripe: unexpected payment method stripe edit state', {
          state: String(state),
        });
        throw new Error('Cannot process payment method');
      }
    }
  }

  private getStripePaymentMethodEditState() {
    return this.pageStore.getState().paymentMethodStripeEditState;
  }

  private getPresavedPaymentToken(): string {
    const token = this.stripeCustomer?.getPaymentMethod()?.getToken();

    if (!token) {
      observabilityAppService.notify('#stripe: customer pre-saved payment method not found');
      throw new Error('No payment method found');
    }

    return token;
  }

  private onShowSavedPaymentMethod() {
    this.pageStore.setState({
      paymentMethodStripeEditState: PaymentMethodStripeEditState.SavedPaymentMethod,
      paymentMethodCanBeUpdated: false,
    });

    paymentMethodsEventBus.emit('paymentMethodCanBeUpdated', false, { source: 'user' });
  }

  private onShowNewPaymentMethodNonEditable() {
    this.pageStore.setState({
      paymentMethodStripeEditState: PaymentMethodStripeEditState.NewPaymentMethodNonEditable,
      paymentMethodCanBeUpdated: true,
    });

    paymentMethodsEventBus.emit('paymentMethodCanBeUpdated', true, { source: 'user' });
  }

  private onShowStripeEditElement() {
    this.pageStore.setState({
      paymentMethodStripeEditState: PaymentMethodStripeEditState.Editable,
      paymentMethodCanBeUpdated: true,
    });

    paymentMethodsEventBus.emit('paymentMethodCanBeUpdated', true, { source: 'user' });
  }

  private async updateStripeLocaleConfig(overrides: Partial<StripeLocaleConfig>) {
    const nextStripeLocaleConfig: StripeLocaleConfig = {
      countryCode: overrides.countryCode ?? currentStripeLocaleConfigState.countryCode,
      currencyCode: overrides.currencyCode ?? currentStripeLocaleConfigState.currencyCode,
    };

    if (!this.haveStripeLocaleConfigsChanged(currentStripeLocaleConfigState, nextStripeLocaleConfig)) {
      return;
    }

    currentStripeLocaleConfigState = nextStripeLocaleConfig;

    this.setIsUpdatingStripeLocaleConfig(true);

    try {
      const updatedCredentials = await this.updateSetupIntentParamsMutation.mutateAsync({
        useCase: this.billingStrategy === BillingStrategy.Stripe ? 'billing' : 'payments-only',
        countryCode: nextStripeLocaleConfig.countryCode ?? undefined,
        currencyCode: nextStripeLocaleConfig.currencyCode ?? undefined,
      });

      if (updatedCredentials) {
        await this.remountStripeElementWithCredentials(updatedCredentials);
      }
    } finally {
      this.setIsUpdatingStripeLocaleConfig(false);
    }
  }

  private haveStripeLocaleConfigsChanged(
    currentStripeLocaleConfig: StripeLocaleConfig,
    nextStripeLocaleConfig: StripeLocaleConfig
  ): boolean {
    return (
      currentStripeLocaleConfig.countryCode !== nextStripeLocaleConfig.countryCode ||
      currentStripeLocaleConfig.currencyCode !== nextStripeLocaleConfig.currencyCode
    );
  }

  private setIsUpdatingStripeLocaleConfig(isUpdating: boolean) {
    this.pageStore.setState({
      paymentMethodsUpdatingStripeLocaleConfig: isUpdating,
    });
  }

  private async remountStripeElementWithCredentials(updatedCredentials: ElementsCredentials) {
    // Update local credentials and fully remount the Payment Element so Stripe can
    // recalculate available payment methods (based on new country/currency config).
    this.elementsCredentials = updatedCredentials;

    const stripeElementFacade = await stripeApi.getStripeElementsFacade();

    stripeElementFacade.destroy();
    await this.mountStripeElement();
  }

  private isUpdatingStripeLocaleConfig(paymentMethodsUpdatingStripeLocaleConfig: boolean) {
    return paymentMethodsUpdatingStripeLocaleConfig;
  }

  /**
   * Refresh Stripe Elements after a payment failure.
   *
   * Fetches fresh SetupIntent credentials from the backend and remounts the Stripe Elements.
   * The fresh credentials will be associated with the current quote ID from the rating query.
   *
   * Note: This should only be called after refetching the rating to ensure we have a fresh
   * quote ID, since the old quote enters 'canceled' status after payment failure.
   *
   * @returns true if refresh succeeded, false if it failed
   * @see CBU-20304
   */
  private async refreshStripeElementsAfterPaymentFailure(): Promise<boolean> {
    observabilityAppService.notify('#stripe: refreshing Stripe Elements after payment failure');

    // Reset the payment state to editable
    this.pageStore.setState({
      paymentMethodStripeState: { type: 'editable' },
      paymentMethodStripeEditState: PaymentMethodStripeEditState.Editable,
    });

    // Fetch fresh SetupIntent credentials from backend
    // This creates a new SetupIntent that is not tainted by the previous failure
    try {
      const updatedCredentials = await this.updateSetupIntentParamsMutation.mutateAsync({
        useCase: 'billing',
        countryCode: currentStripeLocaleConfigState.countryCode ?? undefined,
        currencyCode: currentStripeLocaleConfigState.currencyCode ?? undefined,
      });

      if (updatedCredentials) {
        // Remount Stripe Elements with the fresh credentials
        await this.remountStripeElementWithCredentials(updatedCredentials);

        return true;
      }

      return false;
    } catch (error) {
      // If we fail to get fresh credentials, log the error and return false
      // The caller can then provide guidance to the user
      observabilityAppService.notify('#stripe: failed to refresh Stripe Elements after payment failure', {
        error: error instanceof Error ? error.message : String(error),
      });

      return false;
    }
  }

  /**
   * Handle all cleanup and recovery after a Stripe payment failure.
   *
   * This method orchestrates the full recovery flow:
   * 1. Clears PayPal agreement ID (if any)
   * 2. Refetches rating to get a fresh quote ID (the old quote is canceled after failure)
   * 3. Refreshes Stripe Elements with fresh SetupIntent credentials
   * 4. Scrolls payment form into view
   *
   * @returns true if refresh succeeded, false if it failed (caller should show additional guidance)
   * @see CBU-20304
   */
  private async handleStripePaymentFailure(): Promise<boolean> {
    this.paypal.api().clearPaypalAgreementId();

    // Refetch rating to get a fresh quote ID
    // After a payment failure, the Stripe quote enters 'canceled' status and cannot be finalized.
    // We need a new quote for the next retry attempt.
    if (this.refetchRating) {
      try {
        await this.refetchRating();
        observabilityAppService.notify('#stripe: successfully refetched rating after payment failure');
      } catch (error) {
        observabilityAppService.notify('#stripe: failed to refetch rating after payment failure', {
          error: error instanceof Error ? error.message : String(error),
        });
        // Continue with the flow even if rating refetch fails
        // The user may still be able to retry, though they might encounter the quote error
      }
    }

    const refreshSucceeded = await this.refreshStripeElementsAfterPaymentFailure();

    const stripeElementsFacade = await stripeApi.getStripeElementsFacade();

    stripeElementsFacade.scrollIntoView();

    return refreshSucceeded;
  }
}
